import { useEffect } from "react";
import cloneDeep from "lodash/cloneDeep";

/**
 * useHandleImageChange - Custom hook for handling image changes.
 *
 * This hook uses a side effect to listen for changes in the images,
 * and updates the image processing statuses and comparison items as necessary.
 *
 * @param {Object} props - The properties passed into the function.
 * @param {Array} props.comparisonItems - The current comparison items state.
 * @param {Array} props.imageProcessingStatuses - The current image processing statuses state.
 * @param {function} props.setImageProcessingStatuses - The setter function for the image processing statuses state.
 * @param {function} props.setComparisonItems - The setter function for the comparison items state.
 * @param {Object} props.baseComparisonItem - The base comparison item to reset to when necessary.
 * @param {number} props.index - The index of the current comparison item and image processing status being processed.
 *
 * @returns {void}
 */
export const useHandleImageChange = ({
  comparisonItems,
  calculationProgress,
  imageProcessingStatuses,
  setImageProcessingStatuses,
  setComparisonItems,
  baseComparisonItem,
  index,
}) => {
  useEffect(() => {
    // ImageTwoStatus: Possible status
    // noImageSelected
    // notSentToProcess
    // serverProcessing
    // processed

    const {
      //   calculationComplete,
      //   calculationSuccessful,
      //   calculationPending,
      //   currentStep,
      //   totalSteps,
      //   percentComplete,
      //   stepDescription,
      state,
    } = calculationProgress;

    // Clone the current states to avoid directly mutating state
    let updatedImageProcessingStatuses = cloneDeep(imageProcessingStatuses);
    let updatedComparisonItems = cloneDeep(comparisonItems);

    const fileOne =
      comparisonItems[index] &&
      comparisonItems[index]["fileOne"] &&
      comparisonItems[index]["fileOne"][0];

    const fileTwo =
      comparisonItems[index] &&
      comparisonItems[index]["fileTwo"] &&
      comparisonItems[index]["fileTwo"][0];

    // Analyze the calculation state, and what images are present. Adjust the card loaders accordingly

    if (!fileOne) {
      updatedImageProcessingStatuses[index] = "noImageSelected";
      // Clear file
      updatedComparisonItems[index]["fileTwo"] = [];
    } else if (fileOne && state === "NOT STARTED") {
      updatedImageProcessingStatuses[index] = "notSentToProcess";
    } else if (fileOne && !fileTwo && state === "SUCCESS") {
      updatedImageProcessingStatuses[index] = "notSentToProcess";
    } else if (fileOne && (state === "PENDING" || state === "PROGRESS")) {
      // Currently calculating
      updatedImageProcessingStatuses[index] = "serverProcessing";
    } else if (fileOne && fileTwo && state === "SUCCESS") {
      updatedImageProcessingStatuses[index] = "processed";
    } else if (fileOne && state === "FAILURE") {
      updatedImageProcessingStatuses[index] = "notSentToProcess";
    }

    // Only update state if it has actually changed
    if (
      JSON.stringify(updatedImageProcessingStatuses) !==
      JSON.stringify(imageProcessingStatuses)
    ) {
      setImageProcessingStatuses(updatedImageProcessingStatuses);
    }
    if (
      JSON.stringify(updatedComparisonItems) !== JSON.stringify(comparisonItems)
    ) {
      setComparisonItems(updatedComparisonItems);
    }
  }, [
    // Specify the dependencies for the useEffect hook
    comparisonItems,
    imageProcessingStatuses,
    setImageProcessingStatuses,
    // setComparisonItems,/
    calculationProgress,
    baseComparisonItem,
    index,
  ]);
};
